import { useEffect } from 'react';
import { useGetFeatureFlagExperiment } from 'js/sign-components/common/feature-flag';
import type { FeatureFlags } from 'js/sign-components/common/feature-flags';
import { useLogPAPEvent } from 'hellospa/common/use-log-pap-event';
import { PAP_Exposure_SignGrowthbook } from '@dropbox/pap-events/sign/exposure_sign_growthbook';

type UseGrowthbookExperimentOptions = {
  /**
   * When false, exposure is not logged even if the user is in the experiment.
   * Defaults to true.
   */
  shouldLogExposure?: boolean;
};

function getExposureStorageKey(experimentName: string) {
  return `exposure_${experimentName}`;
}

function getExperimentEnabledState(
  experiment: ReturnType<typeof useGetFeatureFlagExperiment>,
) {
  return experiment && typeof experiment === 'object' && 'status' in experiment
    ? experiment.status
    : !!experiment;
}

/**
 * Generic hook for Growthbook feature flag experiments.
 * Handles feature flag evaluation and automatic exposure tracking with deduplication.
 */
export function useGrowthbookExperiment(
  experimentName: keyof FeatureFlags,
  options: UseGrowthbookExperimentOptions = {},
) {
  const { shouldLogExposure = true } = options;
  const logPAPEvent = useLogPAPEvent();

  const experiment = useGetFeatureFlagExperiment(experimentName);
  const isEnabled = getExperimentEnabledState(experiment);
  const experimentObject = typeof experiment === 'object' ? experiment : null;

  useEffect(() => {
    const storageKey = getExposureStorageKey(experimentName);
    const hasExposureInSession = sessionStorage.getItem(storageKey);

    if (
      experimentObject?.inExperiment &&
      shouldLogExposure &&
      !hasExposureInSession
    ) {
      logPAPEvent(
        PAP_Exposure_SignGrowthbook({
          experimentVariant: (experimentObject.variationId ?? 0).toString(),
          feature: experimentName,
        }),
      );

      sessionStorage.setItem(storageKey, 'true');
    }
  }, [experimentName, experimentObject, shouldLogExposure, logPAPEvent]);

  return {
    isEnabled,
    experiment: experimentObject,
  };
}
